/**
 * CompatibilityBadge - Visual indicator for schema compatibility modes
 *
 * Displays a color-coded badge representing the schema compatibility mode.
 * Color scheme:
 * - BACKWARD: Green - New schema can read old data
 * - FORWARD: Blue - Old schema can read new data
 * - FULL: Purple - Both backward and forward compatible
 * - NONE: Yellow - No compatibility guarantees
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import type { CompatibilityMode } from '@/features/schemas/types/schema.types';

interface CompatibilityBadgeProps {
  /**
   * Compatibility mode to display
   */
  mode: CompatibilityMode;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Get badge styling based on compatibility mode
 */
const getBadgeVariant = (mode: CompatibilityMode) => {
  switch (mode) {
    case 'backward':
      return 'default'; // Green background
    case 'forward':
      return 'secondary'; // Blue background
    case 'full':
      return 'outline'; // Purple border
    case 'none':
      return 'destructive'; // Yellow/Red background
    default:
      return 'default';
  }
};

/**
 * Get custom color classes for compatibility badge
 */
const getColorClasses = (mode: CompatibilityMode): string => {
  switch (mode) {
    case 'backward':
      return 'bg-success/10 text-success border-success/30';
    case 'forward':
      return 'bg-info/10 text-info border-info/30';
    case 'full':
      return 'bg-violet/10 text-violet border-violet/30';
    case 'none':
      return 'bg-warning/10 text-warning border-warning/30';
    default:
      return '';
  }
};

/**
 * Get descriptive tooltip text for compatibility mode
 */
const getTooltipText = (mode: CompatibilityMode): string => {
  switch (mode) {
    case 'backward':
      return 'New schema versions can read data written with old versions';
    case 'forward':
      return 'Old schema versions can read data written with new versions';
    case 'full':
      return 'Both backward and forward compatible - maximum flexibility';
    case 'none':
      return 'No compatibility guarantees - breaking changes allowed';
    default:
      return '';
  }
};

/**
 * CompatibilityBadge Component
 *
 * Renders a styled badge indicating the schema compatibility mode.
 *
 * @example
 * ```tsx
 * <CompatibilityBadge mode="BACKWARD" />
 * <CompatibilityBadge mode="FULL" className="ml-2" />
 * ```
 */
export const CompatibilityBadge: React.FC<CompatibilityBadgeProps> = ({ mode, className }) => {
  const colorClasses = getColorClasses(mode);
  const tooltipText = getTooltipText(mode);

  return (
    <Badge
      variant={getBadgeVariant(mode)}
      className={`${colorClasses} border font-semibold ${className || ''}`}
      title={tooltipText}
    >
      {mode}
    </Badge>
  );
};
